/**
 * -----------------------------------------------------------------------------
 * FleetTags Listing Page
 * -----------------------------------------------------------------------------
 * This page is used to list all the fleettags of the organization.
 */

import { toZonedTime } from "date-fns-tz";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import { useOrganizationFleettagsQuery } from "../../../api/network/adminApiServices";
import {
  setListingQueryParams,
  setModalsData,
  TListingQueryParams,
  TModalsState,
} from "../../../api/store/commonSlice";
import {
  OrganizationFleettag,
  TLegacyFleettag,
} from "../../../api/types/Fleettag";
import { TSelectboxOption } from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import AdminTable from "../../../components/adminTable";
import ListingPageSubHeader from "../../../components/listingPageSubHeader";
import ListingTableHeader from "../../../components/listingTableHeader";
import {
  EditListingColumnsButton,
  ModalEditListingColumns,
  TListingColumn,
} from "../../../components/modalEditListingColumns";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import { TAdminTableRowData } from "../../../components/types";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { useAccountTimezone } from "../../../hooks/timezone";
import { routeUrls } from "../../../navigation/routeUrls";
import { SortingStatusWithReset } from "../../../utils/adminListings";
import {
  convertUnixTimestampToDate,
  dbTimeToJsTime,
} from "../../../utils/dateTime";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import { roundOffLatLong } from "../../../utils/map";
import { useLoggedInUserData } from "../../../utils/user";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";
import AdminsFleetTagsCreateNew from "./createNewFleettag";

const columnOrderingFields: Record<string, string> = {
  "FleetTag ID": "entityid",
  "FleetTag Name": "description",
  "In Range": "isattached",
  "Last Connected/Disconnected Address": "address",
  Timestamp: "timestamp",
  Status: "statuscode",
  "Last Updated": "lastupdatetime",
  // "Last Location", // intentionally excluded from sortability
  "Asset ID": "deviceid",
  "Asset Description": "device_description",
  "Display Name": "device_displayname",
  Driver: "drivername",
};

const ScreenDashboardAdminFleetTags = () => {
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";

  const all_columns = [
    "FleetTag ID",
    "FleetTag Name",
    "In Range",
    "Last Connected/Disconnected Address",
    "Timestamp",
    "Status",
    "Last Updated",
    "Last Location",
    "Asset ID",
    "Asset Description",
    "Display Name",
    ...(showDriver ? ["Driver"] : []),
  ];

  const { t: tMain } = useTranslation();
  const { t: tFilters } = useTranslation("translation", {
    keyPrefix: "admins.filters",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.fleettags",
  });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { fleetTags: orgFleetTagsQueryParams } = listingQueryParams;
  const dispatch = useDispatch();
  const listingWrapperRef = React.useRef<HTMLDivElement>(null);

  // access and permissions
  const { getUserACLAccessLevel } = useACL();
  const thisOrgFleetTagAllowed = useLoggedInUserData("hasfleettags");

  // retrieve account timezone
  const { convertToTZForDateFns, tzForDateFns } = useAccountTimezone();

  // columns
  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem(localStorageKeys.columns.Fleettags);
  const [columns, setColumns] = React.useState<TListingColumn[]>(
    all_columns.map((colItem, _) => {
      return {
        name: colItem,
        show: !!savedColumns ? savedColumns.includes(colItem) : true,
      };
    })
  );

  // preparing query params
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        fleetTags: { ...orgFleetTagsQueryParams, page: 1, search: value },
      })
    );
  }, 500);

  const handleOrderingChange = (ordering?: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        fleetTags: {
          ...orgFleetTagsQueryParams,
          ordering,
          page: 1,
        },
      })
    );
  };

  // fetching fleettags data
  const {
    data: dataOrgFleetTags,
    isFetching: isFetchingOrgFleetTags,
    error,
  } = useOrganizationFleettagsQuery({
    ...orgFleetTagsQueryParams,
    search_fields: APP_CONFIG.ENTITY_SEARCH_FIELDS.FLEETTAG_LISTING.join(","),
  });
  const { count, next, previous, results } = dataOrgFleetTags || {};

  // smooth scroll listing to the top on page change
  useEffect(() => {
    listingWrapperRef?.current?.scrollTo({ behavior: "smooth", top: 0 });
  }, [dataOrgFleetTags]);

  // preparing table data
  const tableData: TAdminTableRowData[] = !!results
    ? (results || ([] as OrganizationFleettag[])).map(
        (item: TLegacyFleettag, index: number) => ({
          navLink: `${routeUrls.dashboardChildren.adminChildren.fleettags}/${item.entityid}`,
          cellData: [
            item?.entityid ?? "-", // "FleetTag ID",
            item?.description ?? "-", // "Name",
            item?.isattached === 1 ? (
              <span className="text-field-success">{tMain("yes")}</span>
            ) : (
              <span className="text-field-error-dark">{tMain("no")}</span>
            ), // "In Range",
            item?.address ?? "-", // "Last Connected/Disconnected Address",
            item?.timestamp ? convertUnixTimestampToDate(item?.timestamp) : "-", // "Timestamp"
            item?.status_descr || `${item?.statuscode}` || "-", // "Status",
            item?.lastupdatetime
              ? // ? convertUnixTimestampToDate(item?.lastupdatetime)
                toZonedTime(
                  dbTimeToJsTime(item?.lastupdatetime),
                  convertToTZForDateFns(tzForDateFns)
                )
                  .toISOString()
                  .replace(".000Z", "")
                  .split("T")
                  .join(" ")
              : "-", // "Last Updated",
            item?.latitude && item?.longitude
              ? `${roundOffLatLong(item?.latitude)},${roundOffLatLong(item?.longitude)}`
              : "-", // "Geopoint",
            item?.deviceid ?? "-", // "Vehicle Id"
            item?.device_description ?? "-", // "Asset Description"
            item?.device_displayname ?? "-", // "Display Name"
            ...(showDriver ? [item?.drivername ?? "-"] : []), // "Driver Name"
          ].filter((_, index) => columns[index].show),
        })
      )
    : [];

  // rendering
  return (
    <>
      <HeaderView title={t("heading")} />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4`}>
        <ListingPageSubHeader
          heading={t("sub_heading")}
          {...(thisOrgFleetTagAllowed === "1" &&
            getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_FLEETTAG) >=
              ROLE_ACCESS_LEVELS.ALL && {
              buttonText: t("add_new"),
              buttonCallback: () => {
                dispatch(
                  setModalsData({ ...modalsState, showCreateFleetTag: true })
                );
              },
            })}
        />
        <ListingTableHeader
          heading={
            <div className="flex items-center gap-4">
              {/* Heading */}
              <span>{t("listing_heading")}</span>

              {/* Sorted by label */}
              {!!orgFleetTagsQueryParams?.ordering && (
                <SortingStatusWithReset
                  ordering={orgFleetTagsQueryParams.ordering}
                  columnOrderingFields={columnOrderingFields}
                  onResetOrdering={() => handleOrderingChange(undefined)}
                />
              )}
            </div>
          }
          searchBoxPlaceholder={t("search_placeholder")}
          searchBoxValue={orgFleetTagsQueryParams.search}
          searchBoxOnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          searchBoxOnClear={() => debouncedSetSearchKeyword("")}
        />
        <ModalEditListingColumns
          columns={columns}
          setColumns={setColumns}
          lsKey={localStorageKeys.columns.Fleettags}
        />
        <div className="py-4 mt-4 relative">
          <EditListingColumnsButton disabled={isFetchingOrgFleetTags} />
          <AdminTable
            viewportHeightOffset={"400"}
            columns={columns
              .filter((item) => item.show)
              .map((item) => item.name)}
            data={tableData}
            isLoading={isFetchingOrgFleetTags}
            listingWrapperRef={listingWrapperRef}
            onOrderingChange={handleOrderingChange}
            ordering={orgFleetTagsQueryParams.ordering}
            sortableColumns={columnOrderingFields}
          />
          {!isFetchingOrgFleetTags && (
            <Pagination
              pageSize={orgFleetTagsQueryParams.page_size}
              handlePageSizeChange={(e: TSelectboxOption | null) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    fleetTags: {
                      ...orgFleetTagsQueryParams,
                      page: 1,
                      page_size: parseInt(`${e?.value}`),
                    },
                  })
                );
              }}
              totalPages={
                count ? Math.ceil(count / orgFleetTagsQueryParams.page_size) : 1
              }
              forcePage={orgFleetTagsQueryParams.page - 1}
              handlePageClick={(data: TPaginationSelected) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    fleetTags: {
                      ...orgFleetTagsQueryParams,
                      page: data?.selected + 1,
                    },
                  })
                );
              }}
              paginCurrentPage={orgFleetTagsQueryParams.page}
              paginItemsOnCurrentPage={results?.length || 0}
              paginTotalItemsCount={count}
            />
          )}
        </div>
      </div>
      <AdminsFleetTagsCreateNew />
    </>
  );
};

export default ScreenDashboardAdminFleetTags;
